import { Car, Wrench, CalendarClock, Gauge, ShieldCheck, ShieldAlert, ShieldX, FileText, History, CheckCircle2, Clock, XCircle, AlertTriangle, CalendarDays, Timer } from "lucide-react";
import { formatDMY } from "@/lib/date";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TONE = {
  grey: { pill: "bg-slate-100 text-slate-600 ring-slate-300", dot: "bg-slate-400", text: "text-slate-600", stroke: "#94a3b8", bar: "bg-slate-400", card: "bg-slate-50", icon: "bg-slate-100 text-slate-500" },
  green: { pill: "bg-emerald-50 text-emerald-700 ring-emerald-300", dot: "bg-emerald-500", text: "text-emerald-600", stroke: "#10b981", bar: "bg-emerald-500", card: "bg-emerald-50/70", icon: "bg-emerald-100 text-emerald-600" },
  amber: { pill: "bg-amber-50 text-amber-700 ring-amber-300", dot: "bg-amber-500", text: "text-amber-600", stroke: "#f59e0b", bar: "bg-amber-500", card: "bg-amber-50/70", icon: "bg-amber-100 text-amber-600" },
  red: { pill: "bg-red-50 text-red-700 ring-red-300", dot: "bg-red-500", text: "text-red-600", stroke: "#ef4444", bar: "bg-red-500", card: "bg-red-50/70", icon: "bg-red-100 text-red-600" },
};
const HIST = {
  Completed: { Icon: CheckCircle2, ring: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700" },
  Scheduled: { Icon: Clock, ring: "bg-slate-500", badge: "bg-slate-100 text-slate-700" },
  Overdue: { Icon: AlertTriangle, ring: "bg-red-500", badge: "bg-red-50 text-red-700" },
  Cancelled: { Icon: XCircle, ring: "bg-slate-300", badge: "bg-slate-100 text-slate-500" },
};
const STATUS_DESC = { "Up to Date": "No maintenance due within 30 days", "Due Soon": "Maintenance due within 30 days", Overdue: "Maintenance is past its due date", "No Record": "No maintenance records yet" };

const d = (iso) => (iso ? formatDMY(iso) : "—");
const klToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuala_Lumpur" }).format(new Date());
const daysUntil = (iso) => Math.round((new Date(`${iso}T00:00:00Z`) - new Date(`${klToday()}T00:00:00Z`)) / 86400000);
const daysLabel = (n) => (n > 0 ? `${n} Day${n === 1 ? "" : "s"} Left` : n === 0 ? "Due Today" : `${Math.abs(n)} Day${n === -1 ? "" : "s"} Overdue`);
const tone = (ms) => TONE[ms.level] || TONE.grey;
const label = (ms) => (ms.has_record ? ms.status : "No Maintenance Record");

const derive = (ms) => {
  const hist = ms.history || [];
  const done = hist.filter((h) => h.status === "Completed" && (h.completion_date || h.service_date));
  const last = done.sort((a, b) => (b.completion_date || b.service_date).localeCompare(a.completion_date || a.service_date))[0];
  const upcoming = hist.filter((h) => ["Scheduled", "Overdue"].includes(h.status) && h.next_maintenance_date)
    .sort((a, b) => a.next_maintenance_date.localeCompare(b.next_maintenance_date))[0];
  const mil = hist.find((h) => h.next_service_mileage != null && h.status !== "Cancelled");
  return {
    last, upcoming,
    upDays: upcoming ? daysUntil(upcoming.next_maintenance_date) : null,
    mileage: mil ? `${Number(mil.next_service_mileage).toLocaleString()} km` : null,
  };
};

const VehicleImg = ({ src, alt, className }) => (
  <div className={`flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-50 ring-1 ring-slate-200 ${className}`}>
    {src ? <img src={src} alt={alt} className="h-full w-full object-contain" /> : <Car className="h-6 w-6 text-slate-400" />}
  </div>
);

const StatusPill = ({ ms, testid }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${tone(ms).pill}`} data-testid={testid}>
    <span className={`h-2 w-2 rounded-full ${tone(ms).dot}`} />{label(ms)}
  </span>
);

const rtTone = (rt) => TONE[rt.level] || TONE.grey;

/* ---------- Hover preview ---------- */
const PreviewLine = ({ Icon, title, children }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#55308E]" />
    <div className="min-w-0 text-xs">
      <p className="text-[#64748B]">{title}</p>
      <p className="font-semibold text-[#0F172A]">{children}</p>
    </div>
  </div>
);

const Preview = ({ ms, img }) => {
  const x = derive(ms);
  return (
    <div className="space-y-3" data-testid={`maint-preview-${ms.vehicle}`}>
      <div className="flex items-center gap-3">
        <VehicleImg src={img} alt={ms.vehicle} className="h-12 w-16" />
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#0F172A]">{ms.vehicle}</p>
          <div className="mt-1"><StatusPill ms={ms} testid={`maint-preview-status-${ms.vehicle}`} /></div>
        </div>
      </div>
      {ms.has_record && (
        <div className="space-y-2">
          <PreviewLine Icon={Wrench} title="Last Completed Service">{x.last ? `${x.last.maintenance_type} · ${d(x.last.completion_date || x.last.service_date)}` : "—"}</PreviewLine>
          <PreviewLine Icon={CalendarClock} title="Next Maintenance">
            {x.upcoming ? <>{x.upcoming.maintenance_type} · <span className={tone(ms).text}>{d(x.upcoming.next_maintenance_date)} ({daysLabel(x.upDays)})</span></> : "—"}
          </PreviewLine>
          {x.mileage && <PreviewLine Icon={Gauge} title="Next Service Mileage">{x.mileage}</PreviewLine>}
        </div>
      )}
      {ms.is_proton && ms.road_tax && (
        <PreviewLine Icon={FileText} title="Road Tax Expiry">
          <span data-testid={`maint-preview-roadtax-${ms.vehicle}`}>{d(ms.road_tax.expiry_date)} <span className={rtTone(ms.road_tax).text}>({ms.road_tax.label})</span></span>
        </PreviewLine>
      )}
      <p className="text-[10px] text-[#94a3b8]">Click for full details</p>
    </div>
  );
};

/* ---------- Popup ---------- */
const InfoCard = ({ Icon, bg, iconCls, title, children, testid }) => (
  <div className={`flex items-start gap-3 rounded-2xl p-4 shadow-sm ring-1 ring-black/5 ${bg}`} data-testid={testid}>
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconCls}`}><Icon className="h-5 w-5" /></span>
    <div className="min-w-0">
      <p className="text-xs font-medium text-[#64748B]">{title}</p>
      {children}
    </div>
  </div>
);

const SectionTitle = ({ Icon, children }) => (
  <p className="mb-2.5 flex items-center gap-2 text-sm font-bold text-[#0F172A]"><Icon className="h-4 w-4 text-[#55308E]" />{children}</p>
);

const Ring = ({ days, pct, stroke }) => {
  const r = 30, c = 2 * Math.PI * r;
  return (
    <div className="relative h-20 w-20 shrink-0" data-testid="roadtax-ring">
      <svg viewBox="0 0 72 72" className="h-full w-full -rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
        <circle cx="36" cy="36" r={r} fill="none" stroke={stroke} strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-lg font-extrabold text-[#0F172A]">{Math.max(days, 0)}</span>
        <span className="text-[10px] font-semibold text-[#64748B]">days</span>
      </div>
    </div>
  );
};

const RoadTaxCard = ({ rt }) => {
  const t = rtTone(rt);
  const has = rt.days_left != null;
  const pct = has ? Math.min(Math.max(rt.days_left, 0) / 365, 1) : 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="maint-popup-roadtax">
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <SectionTitle Icon={FileText}>Road Tax Information</SectionTitle>
          <p className="text-xs text-[#64748B]">Expiry Date</p>
          <p className="text-2xl font-extrabold text-[#55308E]" data-testid="roadtax-expiry">{d(rt.expiry_date)}</p>
          {has && (
            <>
              <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${t.pill}`} data-testid="roadtax-days-badge"><Timer className="h-3.5 w-3.5" />{rt.label}</span>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200"><div className={`h-full rounded-full ${t.bar}`} style={{ width: `${Math.max(pct * 100, rt.days_left > 0 ? 2 : 0)}%` }} data-testid="roadtax-progress" /></div>
              <p className="mt-1 text-[11px] text-[#64748B]">{Math.max(rt.days_left, 0)} days remaining ({Math.round(pct * 100)}% of year)</p>
            </>
          )}
        </div>
        {has && <Ring days={rt.days_left} pct={pct} stroke={t.stroke} />}
      </div>
    </div>
  );
};

const HistoryTimeline = ({ items }) => {
  if (!items.length) return <p className="rounded-xl bg-slate-50 py-5 text-center text-sm text-[#64748B]" data-testid="maint-popup-empty">No Maintenance Record</p>;
  return (
    <ol className="max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm" data-testid="maint-popup-history">
      {items.map((h, i) => {
        const s = HIST[h.status] || HIST.Scheduled;
        return (
          <li key={i} className="relative flex gap-3 py-2.5" data-testid={`maint-history-${i}`}>
            {i < items.length - 1 && <span className="absolute left-[13px] top-9 bottom-0 w-0.5 bg-slate-200" aria-hidden="true" />}
            <span className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white ${s.ring}`}><s.Icon className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#0F172A]">{h.maintenance_type}</p>
              <p className="text-xs text-[#64748B]">Service Date: {d(h.service_date)}<span className="mx-1.5 hidden sm:inline">|</span><br className="sm:hidden" />Next Due: {d(h.next_maintenance_date)}</p>
            </div>
            <span className={`h-fit shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${s.badge}`}>{h.status}</span>
          </li>
        );
      })}
    </ol>
  );
};

const Popup = ({ ms, img, open, onOpenChange }) => {
  const x = derive(ms);
  const t = tone(ms);
  const StatusIcon = ms.level === "red" ? ShieldX : ms.level === "amber" ? ShieldAlert : ShieldCheck;
  const sub = x.upcoming ? `${x.upcoming.maintenance_type} due ${x.upDays >= 0 ? `in ${x.upDays} day${x.upDays === 1 ? "" : "s"}` : `${Math.abs(x.upDays)} day(s) ago`}` : ms.has_record ? "No upcoming maintenance scheduled" : "No maintenance history available";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-2xl gap-5 overflow-y-auto rounded-3xl p-4 sm:p-6" data-testid={`maint-popup-${ms.vehicle}`}>
        <div className="flex items-center gap-4 pr-8">
          <VehicleImg src={img} alt={ms.vehicle} className="h-16 w-24 sm:h-20 sm:w-32" />
          <div className="min-w-0">
            <DialogTitle className="font-heading text-lg font-bold text-[#0F172A] sm:text-xl">{ms.vehicle}</DialogTitle>
            <div className="mt-1.5"><StatusPill ms={ms} testid="maint-popup-status" /></div>
            <DialogDescription className="mt-1.5 text-xs text-[#64748B]">{sub}</DialogDescription>
          </div>
        </div>

        {ms.has_record && (
          <div>
            <SectionTitle Icon={Wrench}>Maintenance Overview</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoCard Icon={Wrench} bg="bg-[#55308E]/[0.06]" iconCls="bg-[#55308E]/10 text-[#55308E]" title="Last Completed Service" testid="maint-popup-last">
                {x.last ? (<><p className="text-base font-bold text-[#0F172A]">{x.last.maintenance_type}</p><p className="flex items-center gap-1 text-xs text-[#475569]"><CalendarDays className="h-3.5 w-3.5" />{d(x.last.completion_date || x.last.service_date)}</p></>) : <p className="text-sm font-semibold text-[#94a3b8]">None yet</p>}
              </InfoCard>
              <InfoCard Icon={CalendarClock} bg="bg-amber-50/70" iconCls="bg-amber-100 text-amber-600" title="Upcoming Maintenance" testid="maint-popup-next">
                {x.upcoming ? (<><p className="text-base font-bold text-[#0F172A]">{x.upcoming.maintenance_type}</p><p className="flex items-center gap-1 text-xs text-[#475569]"><CalendarDays className="h-3.5 w-3.5" />{d(x.upcoming.next_maintenance_date)}</p><p className={`text-xs font-bold ${x.upDays < 0 ? "text-red-600" : x.upDays <= 30 ? "text-amber-600" : "text-emerald-600"}`} data-testid="maint-popup-next-days">{daysLabel(x.upDays)}</p></>) : <p className="text-sm font-semibold text-[#94a3b8]">None scheduled</p>}
              </InfoCard>
              <InfoCard Icon={Gauge} bg="bg-sky-50/80" iconCls="bg-sky-100 text-sky-600" title="Next Service Mileage" testid="maint-popup-mileage">
                <p className="text-base font-bold text-[#0F172A]">{x.mileage || "Not recorded"}</p>
              </InfoCard>
              <InfoCard Icon={StatusIcon} bg={t.card} iconCls={t.icon} title="Maintenance Status" testid="maint-popup-status-card">
                <p className={`text-base font-bold ${t.text}`}>{ms.status}</p>
                <p className="text-xs text-[#64748B]">{STATUS_DESC[ms.status]}</p>
              </InfoCard>
            </div>
          </div>
        )}

        {ms.is_proton && ms.road_tax && <RoadTaxCard rt={ms.road_tax} />}

        <div>
          <SectionTitle Icon={History}>Maintenance History</SectionTitle>
          <HistoryTimeline items={ms.history || []} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MaintenancePopup = Popup;

export const VehicleImageTrigger = ({ ms, img, onOpen, children }) => {
  if (!ms) return <div className="hidden shrink-0 sm:flex">{children}</div>;
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" onClick={onOpen} className="hidden shrink-0 cursor-pointer rounded-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55308E] sm:flex" data-testid={`vehicle-image-btn-${ms.vehicle}`} aria-label={`${ms.vehicle} maintenance details`}>
          {children}
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" sideOffset={10} collisionPadding={12} className="z-50 w-72 rounded-2xl border-slate-200 p-4 shadow-xl">
        <Preview ms={ms} img={img} />
      </HoverCardContent>
    </HoverCard>
  );
};
